import React from 'react';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { CTAButton } from '../../controls/ButtonBase';
import { useLocalization } from '../../context/Localization/localization';
import { Heading } from '../../controls/Heading/Heading';
import RoundedIcon from '../../controls/Icon/RoundedIcon/RoundedIcon';
import { ScrollableBody } from '../Layout/ScrollableBody/ScrollableBody';
import css from './SignElectronically.module.scss';
import classNames from 'classnames';
import Pen from '../../assets/icons/pen.svg?react';
import { GMAppointmentName } from '../../controls/AppointmentName';
import { AppointmentDispatchTypes, useAppointmentDispatch, useDisclaimerInput } from '../../context/Appointment/appointment';
import { Formik } from 'formik';
import { useHideKioskBackground } from '../../context/Page/page';
import { GMButtonGroup } from '../../controls/ButtonGroup';
import { GMSizedIcon } from '../../controls/Icon';
import { GMForm } from '../../controls/Form';

interface IProps {
    onProceed: () => void;
}

export const SignElectronically: React.FC<IProps> = React.memo(({ onProceed }) => {
    useHideKioskBackground()();
    const {
        dealer: {
            consentToSignElectronicallyEnabled,
            localization: { consentToSignMessage },
        },
    } = useKioskState();
    const Localization = useLocalization();
    const appointmentDispatch = useAppointmentDispatch();
    const { gaveConsentToSignElectronically } = useDisclaimerInput();

    if (!consentToSignElectronicallyEnabled) {
        return null;
    }

    return (
        <Formik
            initialValues={{}}
            onSubmit={() => { }}
        >
            <GMForm
                submitButton={
                    gaveConsentToSignElectronically ? (
                        <CTAButton
                            styleType={'primary'}
                            onClick={onProceed}
                            label={Localization.Proceed}
                            title={Localization.ClickToProceedToNextStep}
                            id={Localization.ClickToProceedToNextStep}
                            ariaLabel={Localization.ClickToProceedToNextStep}
                        />
                    ) : (
                        <></>
                    )
                }
            >
                <ScrollableBody
                    hideAccessibilityButton
                    heading={
                        <Heading
                            roundedIcon={
                                <RoundedIcon
                                    roundedBackgroundSize={'large'}
                                    themeType={'secondary'}
                                >
                                    <div className={classNames(css.sizedIconExtraLarge)}>
                                        <Pen title={Localization.PenIcon} />
                                    </div>
                                </RoundedIcon>
                            }
                            primaryHeading={Localization.SignElectronically}
                            showMustache
                        />
                    }
                    proceedButtons={
                        <>
                            <GMButtonGroup
                                alignment={'center'}
                                buttons={[
                                    (
                                        <CTAButton
                                            type={'button'}
                                            styleType={'primary'}
                                            onClick={async () => {
                                                appointmentDispatch({
                                                    type: AppointmentDispatchTypes.GaveConsentToSignElectronically,
                                                    payload: true,
                                                });
                                                onProceed();
                                            }}
                                            label={Localization.IAgree}
                                            kioskOptions={{ square: true }}
                                            title={Localization.ClickToAgreeWithElectronicSignatures}
                                            disabled={gaveConsentToSignElectronically}
                                            id={Localization.ClickToAgreeWithElectronicSignatures}
                                            ariaLabel={Localization.ClickToAgreeWithElectronicSignatures}
                                        />
                                    ),
                                    (
                                        <CTAButton
                                            type={'button'}
                                            styleType={'secondary'}
                                            onClick={async () => {
                                                appointmentDispatch({
                                                    type: AppointmentDispatchTypes.GaveConsentToSignElectronically,
                                                    payload: false,
                                                });
                                                onProceed();
                                            }}
                                            label={Localization.Decline}
                                            kioskOptions={{
                                                square: true,
                                                squareOptions: {
                                                    roundedIcon: (
                                                        <RoundedIcon
                                                            themeType={'primary'}
                                                            allowInvertedPrimary
                                                            roundedBackgroundSize={'large'}
                                                        >
                                                            <GMSizedIcon iconType={'No'} iconSize={'custom'} />
                                                        </RoundedIcon>
                                                    ),
                                                },
                                            }}
                                            title={Localization.ClickToDeclineWithElectronicSignatures}
                                            disabled={gaveConsentToSignElectronically}
                                            id={Localization.ClickToDeclineWithElectronicSignatures}
                                            ariaLabel={Localization.ClickToDeclineWithElectronicSignatures}
                                        />
                                    ),
                                ]}
                            />
                            {gaveConsentToSignElectronically && (
                                <div className={css.youHaveAgreedMessage}>{Localization.YouHaveAgreedToOurUseOfElectronicSignatures}</div>
                            )}
                        </>
                    }
                    unsetButtons
                    topDivider
                    hideSpacer
                    children={
                        <div className={classNames(css.heading)}>
                            <h2 className={css.primary}>
                                <GMAppointmentName />
                            </h2>
                            <h3 className={css.secondary}>{consentToSignMessage}</h3>
                        </div>
                    }
                />
            </GMForm>
        </Formik>
    );
});
